import { z } from 'zod';
import { DomainError } from '../errors.js';
import { MATERIAL_UNITS, toIssues } from '../materials/catalog.js';
import { SERVICE_REGIONS } from '../supplier-portal/regions.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Teklif isteği (RFQ) ve teklif karşılaştırması (ADR 0026 §1; `satin-alma-modulu.md` §5–§6;
 * `satin-alma-akisi.jpeg` "Supplier Comparison"). Onaylanmış talepten ofiste açılır; ticari
 * bilgidir, gemiye inmez. Hata iletileri koddur; arayüz çevirir.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const currency = () => z.string(typed('INVALID_CURRENCY')).regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY');
const days = () =>
  z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(0, 'OUT_OF_RANGE')
    .max(3_650, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null);
const MAX_MONEY = 1_000_000_000;
const money = () =>
  z.number(typed('INVALID_NUMBER')).min(0, 'OUT_OF_RANGE').max(MAX_MONEY, 'OUT_OF_RANGE');

/** En çok davetli tedarikçi: karşılaştırma tablosu tek ekranda okunur kalır. */
export const MAX_RFQ_SUPPLIERS = 20;

/** RFQ başlığı (`satin-alma-modulu.md` §5 "RFQ bilgileri"). Kalemler onaylı talepten kopyalanır. */
export const rfqSchema = z.object({
  requisitionId: z.uuid(typed('INVALID_ID')),
  /** Tekliflerin son günü (Closing Date). */
  closingDate: z.iso.date(typed('INVALID_DATE')),
  currency: currency(),
  deliveryLocation: optionalText(120),
  deliveryRequirement: optionalText(300),
  paymentTerms: optionalText(200),
  warranty: optionalText(200),
  validityDays: days(),
  technicalSpec: optionalText(2000),
  /** Davet edilen tedarikçiler: en az biri; yalnız onaylı ve etkin (sorular #79). */
  supplierIds: z
    .array(z.uuid(typed('INVALID_ID')), typed('NO_SUPPLIERS'))
    .min(1, 'NO_SUPPLIERS')
    .max(MAX_RFQ_SUPPLIERS, 'TOO_MANY_SUPPLIERS')
    .transform((ids) => [...new Set(ids)]),
});
export type Rfq = z.output<typeof rfqSchema>;

export function parseRfq(input: unknown): ParseResult<Rfq> {
  const result = rfqSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Sıradaki RFQ numarası: şirkette yıl içinde sıralı `RFQ-YYYY-NNN` (tasarımda "RFQ-2026-015").
 * RFQ yalnız bulutta açılır; tekillik şirket veritabanında.
 */
export function nextRfqNo(existing: readonly string[], year: number): string {
  let last = 0;
  for (const no of existing) {
    const match = /^RFQ-(\d{4})-(\d+)$/.exec(no);
    if (match && Number(match[1]) === year) last = Math.max(last, Number(match[2]));
  }
  return `RFQ-${year}-${String(last + 1).padStart(3, '0')}`;
}

export const RFQ_STATUSES = ['draft', 'sent', 'selected', 'cancelled'] as const;
export type RfqStatus = (typeof RFQ_STATUSES)[number];

/** RFQ durumu: taslak → tedarikçilere gönderildi ("Sent to Suppliers") → seçildi; ya da iptal. */
export function rfqStatus(rfq: {
  sentAt: string | null;
  selectedQuotationId: string | null;
  cancelledAt: string | null;
}): RfqStatus {
  if (rfq.cancelledAt) return 'cancelled';
  if (rfq.selectedQuotationId) return 'selected';
  return rfq.sentAt ? 'sent' : 'draft';
}

/** Teklif satırı: RFQ kalemine birim fiyat; boş, tedarikçi bu kalemi fiyatlamadı demektir. */
export const quotationLineSchema = z.object({
  rfqLineId: z.uuid(typed('INVALID_ID')),
  unitPrice: money()
    .nullish()
    .transform((value) => value ?? null),
});

/** Tedarikçi teklifi (`satin-alma-modulu.md` §6): ofis, tedarikçiden gelen teklifi girer. */
export const quotationSchema = z.object({
  supplierId: z.uuid(typed('INVALID_ID')),
  quotationRef: optionalText(80),
  receivedDate: z.iso.date(typed('INVALID_DATE')),
  currency: currency(),
  /** Teslim süresi (gün; tasarımda "Delivery Time"). */
  leadTimeDays: days(),
  validityDays: days(),
  warranty: optionalText(200),
  discount: money().default(0),
  freight: money().default(0),
  customs: money().default(0),
  tax: money().default(0),
  serviceFee: money().default(0),
  remarks: optionalText(1000),
  lines: z.array(quotationLineSchema).max(200, 'TOO_MANY_LINES'),
});
export type Quotation = z.output<typeof quotationSchema>;

export function parseQuotation(input: unknown): ParseResult<Quotation> {
  const result = quotationSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

export interface QuotationTotals {
  /** Fiyatlanan kalemlerin toplamı (miktar × birim fiyat). */
  itemsTotal: number;
  /** Toplam teslim maliyeti: kalemler − indirim + navlun + gümrük + vergi + hizmet bedeli. */
  landedTotal: number;
  /** Bütün RFQ kalemleri fiyatlandı mı; eksik teklif karşılaştırmada dışarıda kalır (#78). */
  complete: boolean;
}

/** Teklif toplamı (`satin-alma-modulu.md` §6 "Total landed cost"). */
export function quotationTotals(
  quotation: Pick<Quotation, 'discount' | 'freight' | 'customs' | 'tax' | 'serviceFee'> & {
    lines: readonly { rfqLineId: string; unitPrice: number | null }[];
  },
  rfqLines: readonly { id: string; quantity: number }[],
): QuotationTotals {
  const prices = new Map(quotation.lines.map((line) => [line.rfqLineId, line.unitPrice]));
  let items = 0;
  let complete = rfqLines.length > 0;
  for (const line of rfqLines) {
    const price = prices.get(line.id);
    if (price === null || price === undefined) complete = false;
    else items += line.quantity * price;
  }
  const itemsTotal = round(items);
  const landedTotal = round(
    itemsTotal -
      quotation.discount +
      quotation.freight +
      quotation.customs +
      quotation.tax +
      quotation.serviceFee,
  );
  return { itemsTotal, landedTotal, complete };
}

/**
 * İndirim fiyatlanan kalemlerin toplamını aşamaz (`INVALID_QUOTATION`): yanlış alana yazılan ya
 * da fazla girilen indirim negatif teslim maliyetiyle "Best Price" almasın. Birim fiyat 0
 * bedelsiz kalemdir; boş fiyat "fiyatlanmadı"dır ve toplama girmez.
 */
export function assertQuotationDiscount(
  quotation: Parameters<typeof quotationTotals>[0],
  rfqLines: readonly { id: string; quantity: number }[],
): void {
  const { itemsTotal } = quotationTotals(quotation, rfqLines);
  if (quotation.discount > itemsTotal) {
    throw new DomainError('INVALID_QUOTATION', 'discount:EXCEEDS_ITEMS');
  }
}

export interface ComparedQuotation {
  id: string;
  currency: string;
  landedTotal: number;
  complete: boolean;
  leadTimeDays: number | null;
  /**
   * Tedarikçi bugün onaylı, etkin ve silinmemiş mi (sorular #79): davetten sonra onayı kalkan ya
   * da pasifleşen tedarikçinin teklifi kayıtta kalır ama seçilemez. Verilmezse onaylı sayılır.
   */
  supplierApproved?: boolean;
}

export interface QuotationRank {
  /** Karşılaştırmaya girer mi: bütün kalemler fiyatlı ve RFQ'nun para biriminde (#78). */
  eligible: boolean;
  /** "Best Price": uygun teklifler içinde en düşük toplam teslim maliyeti. */
  bestPrice: boolean;
  /** Uygun teklifler içinde en kısa teslim süresi. */
  fastest: boolean;
  /** Uygun değilse neden: eksik fiyat, başka para birimi ya da onaysız tedarikçi. */
  reason?: 'INCOMPLETE_QUOTATION' | 'QUOTATION_CURRENCY' | 'SUPPLIER_NOT_APPROVED';
}

const leadOf = (q: ComparedQuotation) => q.leadTimeDays ?? Number.POSITIVE_INFINITY;

/**
 * Teklif karşılaştırması (sorular #78): "Best Price" rozeti en düşük toplam teslim maliyetine
 * gider; eşitlikte kısa teslim süresi öne geçer. Fiyatı ve teslim süresi en iyiyle tam eşit
 * tekliflerin hepsi "Best Price"tır: hiçbiri gerekçe istemez, seçim sorgu sırasına bağlı olmaz.
 * Eksik fiyatlı, RFQ'dan başka para birimindeki ya da artık onaylı olmayan tedarikçinin teklifi
 * kur varsayımı yapılmadan dışarıda bırakılır (AMOS, ShipNet ve Mespas'ta karşılaştırma tek para
 * biriminde yapılır; onaysız tedarikçiden alım ISM bulgusudur, #79).
 */
export function compareQuotations(
  rfqCurrency: string,
  quotations: readonly ComparedQuotation[],
): Map<string, QuotationRank> {
  const approved = (q: ComparedQuotation) => q.supplierApproved !== false;
  const eligible = quotations.filter(
    (q) => q.complete && q.currency === rfqCurrency && approved(q),
  );
  const best = [...eligible].sort(
    (a, b) => a.landedTotal - b.landedTotal || leadOf(a) - leadOf(b),
  )[0];
  const isBest = (q: ComparedQuotation) =>
    best !== undefined && q.landedTotal === best.landedTotal && leadOf(q) === leadOf(best);
  const fastestLead = Math.min(...eligible.map(leadOf));
  const result = new Map<string, QuotationRank>();
  for (const q of quotations) {
    if (!approved(q)) {
      result.set(q.id, {
        eligible: false,
        bestPrice: false,
        fastest: false,
        reason: 'SUPPLIER_NOT_APPROVED',
      });
    } else if (!q.complete) {
      result.set(q.id, {
        eligible: false,
        bestPrice: false,
        fastest: false,
        reason: 'INCOMPLETE_QUOTATION',
      });
    } else if (q.currency !== rfqCurrency) {
      result.set(q.id, {
        eligible: false,
        bestPrice: false,
        fastest: false,
        reason: 'QUOTATION_CURRENCY',
      });
    } else {
      result.set(q.id, {
        eligible: true,
        bestPrice: isBest(q),
        fastest: Number.isFinite(fastestLead) && leadOf(q) === fastestLead,
      });
    }
  }
  return result;
}

/**
 * Seçilebilir mi (sorular #78): yalnız uygun teklif; en ucuz olmayan teklifin seçimi gerekçe
 * ister (SERTICA ve BASS'ta "reason for selection"; denetimde iz bırakır [NR496 4.3.2a]).
 */
export function assertSelectable(
  comparison: ReadonlyMap<string, QuotationRank>,
  quotationId: string,
  reason: string | null,
): void {
  const rank = comparison.get(quotationId);
  if (!rank) throw new DomainError('NOT_FOUND', quotationId);
  if (!rank.eligible) throw new DomainError(rank.reason ?? 'INCOMPLETE_QUOTATION', quotationId);
  if (!rank.bestPrice && !reason?.trim()) {
    throw new DomainError('SELECTION_REASON_REQUIRED', quotationId);
  }
}

// --- Tedarikçi portalı: satır yanıtı, yayın ve satır bazında karşılaştırma (ADR 0111 §C) ---

/**
 * Satır yanıtı (sahip, 2026-10-23): fiyatlı, fiyatsız ("fiyatı sonra"), satırı reddetme ya da
 * ikame ürün. Her yanıt kayda geçer; reddedilen ve fiyatsız satır da teklifin parçasıdır.
 */
export const LINE_RESPONSES = ['priced', 'no_price', 'declined', 'substitute'] as const;
export type LineResponse = (typeof LINE_RESPONSES)[number];

const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const unit = () => z.enum(MATERIAL_UNITS, typed('INVALID_UNIT'));
const quantity = () =>
  z.number(typed('INVALID_NUMBER')).gt(0, 'OUT_OF_RANGE').max(1_000_000, 'OUT_OF_RANGE');

/** İkame ürün: ad, özellik, birim ve miktar (sahip, 2026-10-23). */
export const substituteSchema = z.strictObject({
  name: requiredText(200),
  specification: optionalText(1000),
  unit: unit(),
  quantity: quantity(),
});
export type Substitute = z.output<typeof substituteSchema>;

/** Taslağın ikamesi: alanlar henüz eksik olabilir. */
const draftSubstituteSchema = z.strictObject({
  name: optionalText(200),
  specification: optionalText(1000),
  unit: unit()
    .nullish()
    .transform((value) => value ?? null),
  quantity: quantity()
    .nullish()
    .transform((value) => value ?? null),
});

const lineFields = {
  rfqLineId: z.uuid(typed('INVALID_ID')),
  response: z.enum(LINE_RESPONSES, typed('INVALID_RESPONSE')),
  /** Fiyatlı satırda RFQ birimine, ikamede ikamenin birimine birim fiyat. */
  unitPrice: money()
    .nullish()
    .transform((value) => value ?? null),
  note: optionalText(500),
};

/** Yanıtın gerektirdiği alanlar: fiyatlıda fiyat; ikamede ikame ve fiyat; diğerlerinde ikisi de yok. */
function lineIssues(line: {
  response: LineResponse;
  unitPrice: number | null;
  substitute: unknown;
}): Array<{ path: string; code: string }> {
  const issues: Array<{ path: string; code: string }> = [];
  const priced = line.response === 'priced' || line.response === 'substitute';
  if (priced && line.unitPrice === null) issues.push({ path: 'unitPrice', code: 'PRICE_REQUIRED' });
  if (!priced && line.unitPrice !== null) {
    issues.push({ path: 'unitPrice', code: 'PRICE_NOT_ALLOWED' });
  }
  if (line.response === 'substitute' && line.substitute === null) {
    issues.push({ path: 'substitute', code: 'SUBSTITUTE_REQUIRED' });
  }
  if (line.response !== 'substitute' && line.substitute !== null) {
    issues.push({ path: 'substitute', code: 'SUBSTITUTE_NOT_ALLOWED' });
  }
  return issues;
}

export const quoteLineSchema = z
  .strictObject({
    ...lineFields,
    substitute: substituteSchema.nullish().transform((value) => value ?? null),
  })
  .superRefine((line, ctx) => {
    for (const issue of lineIssues(line)) {
      ctx.addIssue({ code: 'custom', message: issue.code, path: [issue.path] });
    }
  });
export type QuoteLine = z.output<typeof quoteLineSchema>;

const draftLineSchema = z.strictObject({
  ...lineFields,
  substitute: draftSubstituteSchema.nullish().transform((value) => value ?? null),
});
export type DraftLine = z.output<typeof draftLineSchema>;

const quoteHeader = {
  quotationRef: optionalText(80),
  /** Teslim süresi (gün). */
  leadTimeDays: days(),
  /** Teklifin geçerliliği (gün). */
  validityDays: days(),
  warranty: optionalText(200),
  discount: money().default(0),
  freight: money().default(0),
  customs: money().default(0),
  tax: money().default(0),
  serviceFee: money().default(0),
  remarks: optionalText(1000),
};

/**
 * Portaldan gönderilen teklif: para birimi RFQ'nunkidir, kur yok (sorular #76); her satır yanıtı
 * kendi kuralına uyar. Kapsam (her RFQ kalemi tam bir kez) `assertQuoteCoversLines`'ta.
 */
export const portalQuoteSchema = z.strictObject({
  ...quoteHeader,
  lines: z.array(quoteLineSchema, typed('INVALID_LINES')).max(200, 'TOO_MANY_LINES'),
});
export type PortalQuote = z.output<typeof portalQuoteSchema>;

/** Taslak: aynı biçim, satır kuralları gönderimde denetlenir. */
export const portalDraftSchema = z.strictObject({
  ...quoteHeader,
  lines: z.array(draftLineSchema, typed('INVALID_LINES')).max(200, 'TOO_MANY_LINES'),
});
export type PortalDraft = z.output<typeof portalDraftSchema>;

export function parsePortalQuote(input: unknown): ParseResult<PortalQuote> {
  const result = portalQuoteSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export function parsePortalDraft(input: unknown): ParseResult<PortalDraft> {
  const result = portalDraftSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Gönderilen teklif her RFQ kalemini tam bir kez yanıtlar (`QUOTE_LINES_MISMATCH`). */
export function assertQuoteCoversLines(
  lines: readonly { rfqLineId: string }[],
  rfqLines: readonly { id: string }[],
): void {
  const wanted = new Set(rfqLines.map((line) => line.id));
  const seen = new Set<string>();
  for (const line of lines) {
    if (!wanted.has(line.rfqLineId) || seen.has(line.rfqLineId)) {
      throw new DomainError('QUOTE_LINES_MISMATCH', line.rfqLineId);
    }
    seen.add(line.rfqLineId);
  }
  if (seen.size !== wanted.size) throw new DomainError('QUOTE_LINES_MISMATCH');
}

/** Satırın yanıtı; ofisin elle girdiği eski satırda (yanıtsız) fiyattan türer. */
export function effectiveResponse(line: {
  response: LineResponse | null;
  unitPrice: number | null;
}): LineResponse {
  if (line.response !== null) return line.response;
  return line.unitPrice === null ? 'no_price' : 'priced';
}

export const RFQ_VISIBILITIES = ['invited', 'open'] as const;
export type RfqVisibility = (typeof RFQ_VISIBILITIES)[number];

/** Son tarih en çok bir yıl ileri (yanlış yıl girişi). */
const MAX_CLOSING_MS = 366 * 24 * 60 * 60 * 1000;

const publicationSchema = z.strictObject({
  visibility: z.enum(RFQ_VISIBILITIES, typed('INVALID_VISIBILITY')),
  /** Son tarih ve saat, UTC ISO-8601 (CLAUDE.md "Zaman"). */
  closingAt: z.iso.datetime(typed('INVALID_DATE')),
  /**
   * Teslim bölgesi (ADR 0113 §2): isteğe bağlı bilgi; tedarikçinin "Open RFQs" listesinde bölge
   * süzgecine girer, görünürlük kapısı değildir (sahip kararı 2026-10-23).
   */
  deliveryRegion: z
    .enum(SERVICE_REGIONS, typed('INVALID_REGION'))
    .nullish()
    .transform((value) => value ?? null),
});
export type RfqPublication = z.output<typeof publicationSchema>;

/**
 * Portala yayın (sahip, 2026-10-23): "açık" (şirketin portala bağlı bütün tedarikçileri) ya da
 * davetli (RFQ'nun seçili tedarikçileri); son tarih gelecekte (`CLOSING_IN_PAST`) ve en çok bir
 * yıl ileride.
 */
export function parseRfqPublication(input: unknown, now: string): ParseResult<RfqPublication> {
  const result = publicationSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  const closing = Date.parse(result.data.closingAt);
  const at = Date.parse(now);
  if (closing <= at) return { ok: false, issues: [{ path: 'closingAt', code: 'CLOSING_IN_PAST' }] };
  if (closing - at > MAX_CLOSING_MS) {
    return { ok: false, issues: [{ path: 'closingAt', code: 'OUT_OF_RANGE' }] };
  }
  return { ok: true, value: { ...result.data, closingAt: new Date(closing).toISOString() } };
}

/**
 * RFQ tedarikçiye portalda görünür mü (ADR 0111 §C, B5): yayımlanmış olmalı; tedarikçi bugün onaylı
 * ve etkin (sorular #79); davetli RFQ'da seçili tedarikçilerden biri. "Açık" RFQ şirketin portala
 * bağlı, onaylı ve etkin bütün tedarikçilerinindir; pazar yeri değildir (sahibe soru, plan §5/1).
 * Bağ (hesap → şirket + tedarikçi) çağıranda çözülür.
 */
export function portalRfqVisible(
  rfq: {
    publishedAt: string | null;
    visibility: RfqVisibility | null;
    cancelledAt?: string | null;
  },
  supplier: { invited: boolean; eligible: boolean },
): boolean {
  if (rfq.publishedAt === null || rfq.visibility === null || !supplier.eligible) return false;
  return rfq.visibility === 'open' || supplier.invited;
}

export const PORTAL_REQUEST_STATUSES = ['open', 'invited', 'submitted', 'closed'] as const;
export type PortalRequestStatus = (typeof PORTAL_REQUEST_STATUSES)[number];

/** Portalda istek durumu: seçilmiş, iptal ya da süresi dolmuş RFQ kapalı; sonra gönderim. */
export function portalRequestStatus(
  rfq: {
    closingAt: string | null;
    rfqStatus: RfqStatus;
    visibility: RfqVisibility;
    submitted: boolean;
  },
  now: string,
): PortalRequestStatus {
  if (rfq.rfqStatus === 'selected' || rfq.rfqStatus === 'cancelled') return 'closed';
  if (rfq.closingAt === null || Date.parse(now) >= Date.parse(rfq.closingAt)) return 'closed';
  return rfq.submitted ? 'submitted' : rfq.visibility;
}

/** "Due in 48h" (plan §4): kapanmamış ve son tarihe 48 saatten az kalmış. */
export const DUE_SOON_MS = 48 * 60 * 60 * 1000;

export function dueSoon(closingAt: string | null, now: string): boolean {
  if (closingAt === null) return false;
  const left = Date.parse(closingAt) - Date.parse(now);
  return left > 0 && left <= DUE_SOON_MS;
}

/** Karşılaştırmaya giren teklif satırı (görünüm tablosundan). */
export interface ComparedLine {
  rfqLineId: string;
  response: LineResponse | null;
  unitPrice: number | null;
  substituteQuantity?: number | null;
  substituteUnitPrice?: number | null;
}

export interface ComparedLineQuotation {
  id: string;
  currency: string;
  /** Tedarikçi bugün onaylı ve etkin mi (sorular #79); verilmezse onaylı. */
  supplierApproved?: boolean;
  lines: readonly ComparedLine[];
}

export interface LineCell {
  quotationId: string;
  /** Yanıt yoksa (teklif bu satırı içermiyor) `null`. */
  response: LineResponse | null;
  /** Fiyatlıda RFQ birimine, ikamede ikame birimine birim fiyat. */
  unitPrice: number | null;
  /** Fiyatlıda RFQ miktarı × fiyat; ikamede ikame miktarı × fiyat. */
  total: number | null;
  substitute: boolean;
  /** Satırın en iyi fiyatı: yalnız fiyatlı, RFQ para biriminde, onaylı tedarikçi. */
  best: boolean;
  eligible: boolean;
  reason?: 'QUOTATION_CURRENCY' | 'SUPPLIER_NOT_APPROVED';
}

export interface LineComparisonRow {
  rfqLineId: string;
  quantity: number;
  cells: LineCell[];
  bestUnitPrice: number | null;
  bestQuotationIds: string[];
  /** Bu satıra önerilen ikame sayısı (ayrı işaretlenir). */
  substitutes: number;
}

export interface LineComparison {
  lines: LineComparisonRow[];
  /** Satır başına en iyi fiyatların toplamı (en iyisi olan satırlar). */
  bestTotal: number;
  /** En iyi fiyatı olan satır sayısı. */
  coveredLines: number;
  /** Teklif başına en iyi fiyatı aldığı satır sayısı. */
  bestCount: Map<string, number>;
}

/**
 * Satır bazında karşılaştırma (sahip, 2026-10-23; ADR 0111 §C): her RFQ kalemi için tekliflerin
 * yanıtları yan yana; satırın en iyisi en düşük birim fiyatlı fiyatlı yanıttır, eşitlerin hepsi
 * en iyidir. İkame ayrı işaretlenir ve en iyi sayılmaz (başka ürün; ofis ayrıca değerlendirir).
 * Fiyatsız ve reddedilen satır en iyi sayılmaz ama kayıtta görünür. Başka para birimi ve onayı
 * kalkan tedarikçi kur varsayımı yapılmadan dışarıda kalır (`compareQuotations` ile aynı ilke).
 * Bütün teklif karşılaştırması (`compareQuotations`) değişmez.
 */
export function compareLines(
  rfqCurrency: string,
  rfqLines: readonly { id: string; quantity: number }[],
  quotations: readonly ComparedLineQuotation[],
): LineComparison {
  const bestCount = new Map<string, number>(quotations.map((q) => [q.id, 0]));
  const byQuotation = new Map(
    quotations.map((q) => [q.id, new Map(q.lines.map((line) => [line.rfqLineId, line]))]),
  );
  let bestTotal = 0;
  let coveredLines = 0;
  const lines = rfqLines.map((rfqLine): LineComparisonRow => {
    const cells = quotations.map((q): LineCell => {
      const reason =
        q.supplierApproved === false
          ? ('SUPPLIER_NOT_APPROVED' as const)
          : q.currency !== rfqCurrency
            ? ('QUOTATION_CURRENCY' as const)
            : undefined;
      const line = byQuotation.get(q.id)?.get(rfqLine.id);
      const base = {
        quotationId: q.id,
        best: false,
        eligible: reason === undefined,
        ...(reason ? { reason } : {}),
      };
      if (!line) {
        return { ...base, response: null, unitPrice: null, total: null, substitute: false };
      }
      const response = effectiveResponse(line);
      if (response === 'substitute') {
        const price = line.substituteUnitPrice ?? null;
        const qty = line.substituteQuantity ?? null;
        return {
          ...base,
          response,
          unitPrice: price,
          total: price === null || qty === null ? null : round(price * qty),
          substitute: true,
        };
      }
      const price = response === 'priced' ? line.unitPrice : null;
      return {
        ...base,
        response,
        unitPrice: price,
        total: price === null ? null : round(price * rfqLine.quantity),
        substitute: false,
      };
    });
    const candidates = cells.filter(
      (cell) => cell.eligible && cell.response === 'priced' && cell.unitPrice !== null,
    );
    const bestUnitPrice = candidates.length
      ? Math.min(...candidates.map((cell) => cell.unitPrice as number))
      : null;
    const bestQuotationIds: string[] = [];
    for (const cell of candidates) {
      if (cell.unitPrice === bestUnitPrice) {
        cell.best = true;
        bestQuotationIds.push(cell.quotationId);
        bestCount.set(cell.quotationId, (bestCount.get(cell.quotationId) ?? 0) + 1);
      }
    }
    if (bestUnitPrice !== null) {
      coveredLines += 1;
      bestTotal += bestUnitPrice * rfqLine.quantity;
    }
    return {
      rfqLineId: rfqLine.id,
      quantity: rfqLine.quantity,
      cells,
      bestUnitPrice,
      bestQuotationIds,
      substitutes: cells.filter((cell) => cell.substitute).length,
    };
  });
  return { lines, bestTotal: round(bestTotal), coveredLines, bestCount };
}
